import { useState } from "react";
import { useAtomValue } from "jotai";
import { useOptionValueApi } from "@/hooks/apiHooks/useOptionValueApi";
import { Button, Label } from "@common/index";
import { Input } from "@common/Form/Input";
import type { OptionValueDto } from "@models/option";
import { optionsAtom } from "@/store/optionAtoms";
import { ComponentColor } from "@common/types";

export const OptionValue = () => {
    const { getOptionValues, createOptionValue } = useOptionValueApi();
    const options = useAtomValue(optionsAtom);
    const [selectedOptionId, setSelectedOptionId] = useState<string>("");
    const [optionValues, setOptionValues] = useState<OptionValueDto[]>([]);
    const [value, setValue] = useState("");

    const handleGetOptionValues = async () => {
        if (!selectedOptionId) return;
        const response = await getOptionValues(parseInt(selectedOptionId));
        setOptionValues(response);
    };

    const handleCreate = async () => {
        if (!selectedOptionId || !value.trim()) return;
        await createOptionValue(parseInt(selectedOptionId), { value: value.trim() });
        setValue("");
        await handleGetOptionValues();
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md max-w-4xl">
            <h2 className="text-xl font-bold mb-4">Option Values</h2>

            <div className="space-y-4 mb-6">
                <div>
                    <Label>Select Option *</Label>
                    <select
                        className="select select-bordered w-full"
                        value={selectedOptionId}
                        onChange={(e) => {
                            setSelectedOptionId(e.target.value);
                            setOptionValues([]);
                        }}
                    >
                        <option value="">-- select an option --</option>
                        {options.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                                {opt.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <Label>Value *</Label>
                    <Input
                        name="value"
                        type="text"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="Enter option value"
                    />
                </div>

                <div className="flex gap-2">
                    <Button
                        onClick={handleGetOptionValues}
                        color={ComponentColor.Secondary}
                        disabled={!selectedOptionId}
                    >
                        Get All Values
                    </Button>
                    <Button
                        onClick={handleCreate}
                        color={ComponentColor.Primary}
                        disabled={!selectedOptionId}
                    >
                        Create Value
                    </Button>
                </div>
            </div>

            {optionValues.length > 0 && (
                <div className="mt-6 border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3">Values:</h3>
                    <div className="bg-gray-50 p-4 rounded text-left">
                        {optionValues.map((ov) => (
                            <div key={ov.id} className="py-1">
                                <span className="font-medium">ID: {ov.id}</span> - {ov.value}{" "}
                                <span className="text-sm text-gray-500">
                                    ({ov.isActive ? "active" : "inactive"})
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
